/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, March 2020
 */
import { difference } from '../collections/operators';
import { Signal } from '../signals/Signal';
import { ISignaler } from '../signals/types';
import {
    QueueStatus,
    DEFAULT_MAX_AGE,
    DEFAULT_RETRIES,
    DEFAULT_BACKOFF,
    DEFAULT_INTERVAL,
    DEFAULT_CONCURRENCY
} from './constants';
import { Task } from './Task';
import { IQueueStorage } from './types';

/**
 * Available queue options.
 */
export interface IQueueOptions<T> {
    /**
     * The name of the queue.
     */
    name: string;
    /**
     * An IQueueStorage object used to persist queue data for reliability.
     */
    queueStorage: IQueueStorage<T>;
    /**
     * Maximum age of task for it to be considered stale.
     */
    maxAge?: number;
    /**
     * Number of times to retry a job after it fails.
     */
    retries?: number;
    /**
     * Number of milliseconds to wait before retrying a failed job.
     */
    backoff?: number;
    /**
     * The number of milliseconds to wait before a job is executed.
     */
    interval?: number;
    /**
     * Number of jobs simultaneosly passed to processors.
     */
    concurrency?: number;
}

/**
 * A function to process tasks in the queue.
 */
export type Processor<T> = (records: T[]) => void | Promise<void>;

/**
 * A simple task queue.
 */
export class Queue<T> implements ISignaler {
    /**
     * Name of the queue.
     */
    public readonly name: string;

    private maxAge: number;
    private backoff: number;
    private retries: number;
    private interval: number;
    private concurrency: number;
    private queueStorage: IQueueStorage<T>;

    private retriesCount = 0;
    private tasks: Task<T>[] = [];
    private failed: Task<T>[] = [];
    private status: QueueStatus = QueueStatus.IDLE;
    private processors: Processor<T>[] = [];

    public readonly signals = {
        error: new Signal<Error>()
    };

    /**
     * Create a Queue instance.
     *
     * @param options Queue options.
     */
    constructor(options: IQueueOptions<T>) {
        this.name = options.name;
        this.queueStorage = options.queueStorage;
        this.maxAge = options.maxAge ?? DEFAULT_MAX_AGE;
        this.retries = options.retries ?? DEFAULT_RETRIES;
        this.backoff = options.backoff ?? DEFAULT_BACKOFF;
        this.interval = options.interval ?? DEFAULT_INTERVAL;
        this.concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;

        this.poll();
        this.tasks = this.pruneTasks(this.queueStorage.getTasks(this.name));
        this.failed = this.pruneTasks(this.queueStorage.getFailed(this.name));
    }

    /**
     * Add an item to the queue.
     *
     * @param data Data associated with the task.
     */
    public push(data: T): void {
        this.tasks.push(new Task(data));
        this.queueStorage.setTasks(this.name, this.tasks);
    }

    /**
     * Add a processing function to the queue.
     * This will be called every `interval` milliseconds or when the queu flushes.
     *
     * @param processor Processor function.
     */
    public process(processor: Processor<T>): void {
        this.processors.push(processor);
    }

    private poll(): void {
        setTimeout(() => {
            try {
                this.nextTick();
            } catch (error) {
                this.signals.error.emit(error);
            }
            this.poll();
        }, this.interval);
    }

    private async flush(tasks: Task<T>[]): Promise<void> {
        this.setStatus(QueueStatus.PROCESSING);

        try {
            const data = tasks.map(t => t.data);
            await Promise.all(this.processors.map(processor => processor(data)));

            this.setTasks(difference(this.tasks, tasks));
            this.setStatus(QueueStatus.IDLE);
        } catch (e) {
            if (this.retriesCount < this.retries) {
                this.retriesCount += 1;
                setTimeout(() => {
                    this.flush(tasks);
                }, this.backoff);
            } else {
                const stackTrace = e.stack || e.message;
                for (const task of tasks) {
                    task.stackTrace = stackTrace;
                    this.failed.push(task);
                }
                this.retriesCount = 0;

                this.setTasks(difference(this.tasks, tasks));
                this.setStatus(QueueStatus.IDLE);
                this.setFailed(this.failed);
            }
        }
    }

    private async nextTick(): Promise<void> {
        const { tasks, status } = this;

        this.setTasks(this.pruneTasks(this.tasks));
        this.setFailed(this.pruneTasks(this.failed));

        if (
            tasks.length === 0 ||
            status === QueueStatus.PROCESSING ||
            this.processors.length === 0
        ) {
            return;
        }

        const records = this.tasks.slice(0, this.concurrency);
        await this.flush(records);
    }

    private setTasks(tasks: Task<T>[]): void {
        this.tasks = tasks;
        this.queueStorage.setTasks(this.name, tasks);
    }

    private setStatus(status: QueueStatus): void {
        this.status = status;
        this.queueStorage.setStatus(this.name, status);
    }

    private setFailed(tasks: Task<T>[]): void {
        this.failed = tasks;
        this.queueStorage.setFailed(this.name, tasks);
    }

    private pruneTasks(tasks: Task<T>[]): Task<T>[] {
        return tasks.filter(task => !task.isStale(this.maxAge));
    }
}



// WEBPACK FOOTER //
// ./src/packages/core/lib/queue/Queue.ts